import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { InputFile } from "@/components/ui/input-file";
import { ICalendarEvent } from "@/interfaces/dashboard.interface";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addEventSchema, AddEventFormData } from "./schemas";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Check, Trash2 } from "lucide-react";
import { cn } from "@/lib/class-control";

interface EditEventDialogProps {
  open: boolean;
  event: ICalendarEvent | null;
  onClose: () => void;
  onUpdate: (event: ICalendarEvent) => void;
  onDelete: (eventId: string) => void;
}

export function EditEventDialog({ open, event, onClose, onUpdate, onDelete }: EditEventDialogProps) {
  const [startDateCalendarOpen, setStartDateCalendarOpen] = useState(false);
  const [endDateCalendarOpen, setEndDateCalendarOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const form = useForm<AddEventFormData>({
    resolver: zodResolver(addEventSchema),
    defaultValues: {
      title: "",
      description: "",
      date: new Date(),
      startTime: "",
      endDate: new Date(),
      endTime: "",
      ordemDoDia: undefined,
      analiseTecnica: undefined,
      outrosAnexos: undefined,
    },
  });

  // Populate form with event data when event changes
  useEffect(() => {
    if (event && open) {
      form.reset({
        title: event.title,
        description: event.description,
        date: new Date(event.date),
        startTime: event.startTime,
        endDate: new Date(event.endDate),
        endTime: event.endTime,
        ordemDoDia: undefined,
        analiseTecnica: undefined,
        outrosAnexos: undefined,
      });
      setShowDeleteConfirm(false);
    }
  }, [event, open, form]);

  const handleSubmit = (data: AddEventFormData) => {
    if (!event) return;

    const updatedEvent: ICalendarEvent = {
      ...event,
      title: data.title,
      description: data.description,
      date: data.date.toISOString().split("T")[0],
      startTime: data.startTime,
      endDate: data.endDate.toISOString().split("T")[0],
      endTime: data.endTime,
      // TODO: Handle file uploads - preserve existing URLs if no new file, or upload new file
      // ordemDoDia: data.ordemDoDia ? uploadedUrl : event.ordemDoDia,
      // analiseTecnica: data.analiseTecnica ? uploadedUrl : event.analiseTecnica,
      // outrosAnexos: data.outrosAnexos ? uploadedUrl : event.outrosAnexos,
    };

    onUpdate(updatedEvent);
    onClose();
  };

  const handleDelete = () => {
    if (!event) return;
    onDelete(event.id);
    onClose();
    setShowDeleteConfirm(false);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#15203C] border-[#3D3D3D] text-white max-w-[1010px]">
        <DialogHeader>
          <DialogTitle className="text-[24px] font-bold text-white">
            Editar Evento
          </DialogTitle>
        </DialogHeader>

        <div className="h-0 border-b border-gray-800/60 w-full" />

        {!showDeleteConfirm ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-[14px]">
              {/* Title */}
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[16px] font-bold text-white">
                      Nome do Evento
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Digite o nome"
                        className="bg-[#15203C] border-[#5D5D5D] h-[59px] px-[20px] py-[10px] text-white placeholder:text-[#D1D1D1]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[16px] font-bold text-white">
                      Descrição do Evento
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Digite o nome"
                        className="bg-[#15203C] border-[#5D5D5D] h-[59px] px-[20px] py-[10px] text-white placeholder:text-[#D1D1D1]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Start Date and Time Row */}
              <div className="grid grid-cols-2 gap-[14px]">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-[16px] font-bold text-white">
                        Data de Início
                      </FormLabel>
                      <Popover open={startDateCalendarOpen} onOpenChange={setStartDateCalendarOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal bg-[#0C1427] border-[#5D5D5D] h-[59px] px-[20px] text-white hover:bg-[#0C1427]/80",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 size-4" />
                              {field.value ? formatDate(field.value) : "dd/mm/aaaa"}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#15203C] border-[#3D3D3D]" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                              setStartDateCalendarOpen(false);
                            }}
                            className="rounded-md"
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="startTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[16px] font-bold text-white">
                        Hora de Início
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="time"
                          placeholder="00:00"
                          className="bg-[#0C1427] border-[#5D5D5D] h-[59px] px-[20px] py-[10px] text-white placeholder:text-[#D1D1D1]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* End Date and Time Row */}
              <div className="grid grid-cols-2 gap-[14px]">
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-[16px] font-bold text-white">
                        Data de Término
                      </FormLabel>
                      <Popover open={endDateCalendarOpen} onOpenChange={setEndDateCalendarOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal bg-[#0C1427] border-[#5D5D5D] h-[59px] px-[20px] text-white hover:bg-[#0C1427]/80",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 size-4" />
                              {field.value ? formatDate(field.value) : "dd/mm/aaaa"}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#15203C] border-[#3D3D3D]" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                              setEndDateCalendarOpen(false);
                            }}
                            className="rounded-md"
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[16px] font-bold text-white">
                        Hora de Término
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="time"
                          placeholder="00:00"
                          className="bg-[#0C1427] border-[#5D5D5D] h-[59px] px-[20px] py-[10px] text-white placeholder:text-[#D1D1D1]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* File Upload Row 1 */}
              <div className="grid grid-cols-2 gap-[29px]">
                <FormField
                  control={form.control}
                  name="ordemDoDia"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[16px] font-bold text-white">
                        Ordem do Dia
                      </FormLabel>
                      <FormControl>
                        <InputFile field={field} placeholder="Faça upload (JPG, PDF, PNG)" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="analiseTecnica"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[16px] font-bold text-white">
                        Análise Técnica
                      </FormLabel>
                      <FormControl>
                        <InputFile field={field} placeholder="Faça upload (JPG, PDF, PNG)" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* File Upload Row 2 */}
              <FormField
                control={form.control}
                name="outrosAnexos"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[16px] font-bold text-white">
                      Outros Anexos
                    </FormLabel>
                    <FormControl>
                      <InputFile field={field} placeholder="Faça upload (JPG, PDF, PNG)" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="flex justify-between gap-[10px] sm:gap-[10px] mt-[26px]">
                <Button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-[#DC4426] text-[#F5F2FF] hover:bg-[#C33A1F] h-[50px] px-[10px] text-[18px] font-bold flex items-center gap-[3px]"
                >
                  <Trash2 className="size-6" />
                  Excluir
                </Button>
                <div className="flex gap-[10px]">
                  <Button
                    type="button"
                    onClick={onClose}
                    className="bg-[#DC4426] text-[#F5F2FF] hover:bg-[#C33A1F] h-[50px] px-[10px] text-[18px] font-bold"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#13D480] text-[#F5F2FF] hover:bg-[#11BD72] h-[50px] px-[10px] text-[18px] font-bold flex items-center gap-[3px]"
                  >
                    <Check className="size-6" />
                    Salvar
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        ) : (
          <div className="flex flex-col gap-[20px] py-[20px]">
            <p className="text-center text-[16px] text-white">
              Tem certeza que deseja excluir este evento?
            </p>
            <p className="text-center text-[14px] text-white/70">
              Esta ação não pode ser desfeita.
            </p>
            <DialogFooter className="flex gap-[10px] sm:gap-[10px]">
              <Button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="bg-[#DC4426] text-[#F5F2FF] hover:bg-[#C33A1F] h-[50px] px-[10px] text-[18px] font-bold"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                className="bg-[#DC4426] text-[#F5F2FF] hover:bg-[#C33A1F] h-[50px] px-[10px] text-[18px] font-bold flex items-center gap-[3px]"
              >
                <Trash2 className="size-6" />
                Confirmar Exclusão
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
